const API_BASE_URL = process.env.NEXT_PUBLIC_API_BASE_URL || 'http://localhost:8080/api/v1';

export type PayrollType = 'SIMPLE' | 'COMPREHENSIVE';

// ── Fetch helpers ───────────────────────────────────────────────────────────────
// Centralised request plumbing so every endpoint shares identical error handling,
// header defaults and JSON/blob parsing instead of repeating the boilerplate.
const JSON_HEADERS = { 'Content-Type': 'application/json' };

function authHeaders(token: string): Record<string, string> {
  return { ...JSON_HEADERS, Authorization: `Bearer ${token}` };
}

type ApiRequestInit = RequestInit & { errorMessage?: string };

async function apiFetch(endpoint: string, options: ApiRequestInit = {}): Promise<Response> {
  const { errorMessage, ...init } = options;
  const response = await fetch(`${API_BASE_URL}${endpoint}`, init);
  if (!response.ok) {
    const body = await response.json().catch(() => null);
    throw new Error(body?.message || errorMessage || 'Request failed');
  }
  return response;
}

async function requestJson<T>(endpoint: string, options: ApiRequestInit = {}): Promise<T> {
  const response = await apiFetch(endpoint, options);
  return response.json();
}

async function requestBlob(endpoint: string, options: ApiRequestInit = {}): Promise<Blob> {
  const response = await apiFetch(endpoint, options);
  return response.blob();
}

/** Serialises a JSON POST body with the correct content-type header. */
function jsonPost(body: unknown, errorMessage?: string): ApiRequestInit {
  return { method: 'POST', headers: JSON_HEADERS, body: JSON.stringify(body), errorMessage };
}

// ── Auth Types ────────────────────────────────────────────────────────────────
export interface LoginRequest {
  email: string;
  password: string;
}

export interface AuthResponse {
  accessToken: string;
  refreshToken: string;
  user: {
    id: number;
    email: string;
    fullName: string;
    role: string;
  };
}

// ── Auth API ──────────────────────────────────────────────────────────────────
export const authApi = {
  login(request: LoginRequest): Promise<AuthResponse> {
    return requestJson('/auth/login', jsonPost(request, 'Invalid email or password'));
  },

  refresh(refreshToken: string): Promise<AuthResponse> {
    return requestJson('/auth/refresh', jsonPost({ refreshToken }, 'Session expired'));
  },

  async logout(refreshToken: string): Promise<void> {
    await fetch(`${API_BASE_URL}/auth/logout`, jsonPost({ refreshToken })).catch(() => {});
  },
};

export interface CalculationRequest {
  calculationType: 'GROSS_TO_NET' | 'NET_TO_GROSS' | 'TAXABLE_AMOUNT';
  grossSalary?: number;
  targetNetSalary?: number;
  taxableAmount?: number;
  nonCashBenefits?: number;
  pensionContribution?: number;
}

export interface TaxBandDetail {
  from: number;
  to: number | null;
  taxablePay: number;
  rate: number;
  taxCharged: number;
}

export interface CalculationResponse {
  grossSalary: number | null;
  nonCashBenefits: number | null;
  pensionContribution: number | null;
  deductions: {
    nssf: number;
    nhif: number;
    housingLevy: number;
    paye: number;
  } | null;
  taxDetails: {
    taxableIncome: number;
    grossPaye: number;
    personalRelief: number;
    netPaye: number;
    deductiblePension: number | null;
  };
  netSalary: number | null;
  taxBandBreakdown: TaxBandDetail[] | null;
}

export interface BulkProcessResponse {
  employeeCount: number;
  analytics: {
    totalGross: number;
    totalNet: number;
    totalPAYE: number;
    totalNHIF: number;
    totalNSSF: number;
    totalHousingLevy: number;
  };
  employees: Array<Record<string, string | number>>;
  columns: string[];
  companyName: string;
  payrollDate: string;
}

export const payrollApi = {
  calculateSalary(request: CalculationRequest): Promise<CalculationResponse> {
    return requestJson('/payroll/calculate', jsonPost(request, 'Failed to calculate salary'));
  },

  generatePayslipPdf(request: {
    employeeName: string;
    idNumber?: string;
    kraPin?: string;
    payrollNumber?: string;
    companyName?: string;
    payrollPeriod?: string;
    basicSalary: number;
    nonCashBenefits?: number;
    pensionContribution?: number;
    customEarnings?: { label: string; amount: number }[];
    customDeductions?: { label: string; amount: number }[];
  }): Promise<Blob> {
    return requestBlob('/payroll/payslip', jsonPost(request, 'Failed to generate payslip'));
  },

  downloadTemplate(payrollType: PayrollType, includeStatutory: boolean): Promise<Blob> {
    return requestBlob(
      '/payroll/bulk/template',
      jsonPost({ payrollType, includeStatutory }, 'Failed to download template'),
    );
  },

  processBulkPayroll(
    file: File,
    payrollType: PayrollType,
    includeStatutory: boolean
  ): Promise<BulkProcessResponse> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('payrollType', payrollType);
    formData.append('includeStatutory', includeStatutory.toString());

    return requestJson('/payroll/bulk/process', {
      method: 'POST',
      body: formData,
      errorMessage: 'Failed to process bulk payroll',
    });
  },

  downloadReports(
    employees: BulkProcessResponse['employees'],
    analytics: BulkProcessResponse['analytics'],
    reportTypes: {
      payrollType: PayrollType;
      statutoryReports: boolean;
      individualPayslips: boolean;
    },
    companyName: string,
    payrollDate: string
  ): Promise<Blob> {
    return requestBlob(
      '/payroll/bulk/reports',
      jsonPost(
        { employees, analytics, reportTypes, companyName, payrollDate },
        'Failed to generate reports',
      ),
    );
  },

  downloadPayslips(
    employees: BulkProcessResponse['employees'],
    analytics: BulkProcessResponse['analytics'],
    reportTypes: {
      payrollType: PayrollType;
      statutoryReports: boolean;
      individualPayslips: boolean;
    },
    companyName: string,
    payrollDate: string
  ): Promise<Blob> {
    return requestBlob(
      '/payroll/bulk/payslips',
      jsonPost(
        { employees, analytics, reportTypes, companyName, payrollDate },
        'Failed to generate payslips',
      ),
    );
  },
};

// ── Blog Types ────────────────────────────────────────────────────────────────
export interface BlogPostSummary {
  slug: string;
  title: string;
  description: string;
  date: string;
  readTime: string;
  author: string;
  category: string;
  coverGradient: string;
  tags: string[];
}

export interface BlogSection {
  type: 'heading' | 'paragraph' | 'list' | 'table' | 'callout' | 'formula' | 'subheading';
  content?: string;
  items?: string[];
  rows?: string[][];
  headers?: string[];
  variant?: 'info' | 'warning' | 'tip' | 'important';
}

export interface BlogPostDetail extends BlogPostSummary {
  id: number;
  sections: BlogSection[];
  published: boolean;
}

// ── Blog Cache Helpers ────────────────────────────────────────────────────────
function blogCacheGet<T>(key: string): { data: T; etag: string } | null {
  try {
    const raw = localStorage.getItem(key);
    if (raw) return JSON.parse(raw);
  } catch { /* ignore */ }
  return null;
}

function blogCacheSet<T>(key: string, data: T, etag: string): void {
  try {
    localStorage.setItem(key, JSON.stringify({ data, etag }));
  } catch { /* quota exceeded — non-critical */ }
}

/**
 * ETag-aware cached GET. Serves cached data on 304 (or network failure) and
 * returns `notFound` for 404 responses so callers can distinguish missing data.
 */
async function cachedGet<T>(endpoint: string, cacheKey: string, notFound?: T): Promise<T> {
  const cached = blogCacheGet<T>(cacheKey);
  const headers: HeadersInit = {};
  if (cached?.etag) headers['If-None-Match'] = cached.etag;

  try {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, { headers });
    if (response.status === 304 && cached) return cached.data;
    if (response.status === 404 && notFound !== undefined) return notFound;
    if (!response.ok) throw new Error('Failed to fetch blog content');

    const data: T = await response.json();
    const etag = response.headers.get('etag') || String(Date.now());
    blogCacheSet(cacheKey, data, etag);
    return data;
  } catch (err) {
    if (cached) return cached.data;
    throw err;
  }
}

// ── Blog API ──────────────────────────────────────────────────────────────────
export const blogApi = {
  getAllPosts(): Promise<BlogPostSummary[]> {
    return cachedGet<BlogPostSummary[]>('/blog', 'blog-cache-all');
  },

  getPost(slug: string): Promise<BlogPostDetail | null> {
    return cachedGet<BlogPostDetail | null>(
      `/blog/${encodeURIComponent(slug)}`,
      `blog-cache-${slug}`,
      null,
    );
  },
};

// ── Admin Types ───────────────────────────────────────────────────────────────
export interface PayrollConfigItem {
  id: number;
  configKey: string;
  configValue: string;
  description: string | null;
  updatedAt: string;
}

export interface PayrollUploadItem {
  id: number;
  companyName: string | null;
  payrollDate: string | null;
  payrollType: string;
  includeStatutory: boolean;
  employeeCount: number;
  originalFileName: string | null;
  employees: Record<string, string | number>[] | null;
  analytics: {
    totalGross: number;
    totalNet: number;
    totalPAYE: number;
    totalNHIF: number;
    totalNSSF: number;
    totalHousingLevy: number;
  } | null;
  columns: string[] | null;
  createdAt: string;
}

export interface BlogPostRequest {
  title: string;
  slug: string;
  description: string;
  publishedDate: string;
  readTime: string;
  author: string;
  category: string;
  coverGradient: string;
  tags: string;
  sections: string;
  published: boolean;
}

// ── Admin API ─────────────────────────────────────────────────────────────────
export const adminApi = {
  // Config
  getConfigs(token: string): Promise<PayrollConfigItem[]> {
    return requestJson('/admin/config', {
      headers: authHeaders(token),
      errorMessage: 'Failed to fetch configs',
    });
  },

  updateConfig(token: string, id: number, value: string, description?: string): Promise<PayrollConfigItem> {
    return requestJson(`/admin/config/${id}`, {
      method: 'PUT',
      headers: authHeaders(token),
      body: JSON.stringify({ value, description }),
      errorMessage: 'Failed to update config',
    });
  },

  // Blog
  getBlogPosts(token: string): Promise<BlogPostDetail[]> {
    return requestJson('/admin/blog', {
      headers: authHeaders(token),
      errorMessage: 'Failed to fetch blog posts',
    });
  },

  getBlogPost(token: string, id: number): Promise<BlogPostDetail> {
    return requestJson(`/admin/blog/${id}`, {
      headers: authHeaders(token),
      errorMessage: 'Failed to fetch blog post',
    });
  },

  createBlogPost(token: string, request: BlogPostRequest): Promise<BlogPostDetail> {
    return requestJson('/admin/blog', {
      method: 'POST',
      headers: authHeaders(token),
      body: JSON.stringify(request),
      errorMessage: 'Failed to create blog post',
    });
  },

  updateBlogPost(token: string, id: number, request: BlogPostRequest): Promise<BlogPostDetail> {
    return requestJson(`/admin/blog/${id}`, {
      method: 'PUT',
      headers: authHeaders(token),
      body: JSON.stringify(request),
      errorMessage: 'Failed to update blog post',
    });
  },

  async deleteBlogPost(token: string, id: number): Promise<void> {
    await apiFetch(`/admin/blog/${id}`, {
      method: 'DELETE',
      headers: authHeaders(token),
      errorMessage: 'Failed to delete blog post',
    });
  },

  // Uploads
  getUploads(token: string): Promise<PayrollUploadItem[]> {
    return requestJson('/admin/uploads', {
      headers: authHeaders(token),
      errorMessage: 'Failed to fetch uploads',
    });
  },

  getUpload(token: string, id: number): Promise<PayrollUploadItem> {
    return requestJson(`/admin/uploads/${id}`, {
      headers: authHeaders(token),
      errorMessage: 'Failed to fetch upload',
    });
  },

  async deleteUpload(token: string, id: number): Promise<void> {
    await apiFetch(`/admin/uploads/${id}`, {
      method: 'DELETE',
      headers: authHeaders(token),
      errorMessage: 'Failed to delete upload',
    });
  },

  exportUpload(token: string, id: number): Promise<Blob> {
    return requestBlob(`/admin/uploads/${id}/export`, {
      headers: authHeaders(token),
      errorMessage: 'Failed to export upload',
    });
  },
};
